import React from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { buildProductUrl } from "../../../utils/navigation";
import { useAuth } from "../../../hooks/useAuth";
import { addProductToCart } from "../../../hooks/useSupabase";
import { ProductCard } from "./Cards/ProductCard";
import { ProductListCard } from "./Cards/ProductListCard";
import type { Product, ProductGridProps } from "./Types";
import { useWishlistContext } from "../../../hooks/useWishlistContext";
import { useCartStore } from "../../../store/cartStore";

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  viewMode,
  loading,
  isShowWishlist = false,
  is_HScroll = false,
  filtering = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = React.useState<{ [id: number]: boolean }>({});
  const { wishlistedIds } = useWishlistContext();
  const addToCartStore = useCartStore((s) => s.addToCart);
  const dbSyncTimers = React.useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const handleAddToCart = (product: Product) => {
    // Variant products: defer to detail page so user picks a specific SKU.
    if (product.has_variants) {
      navigate(buildProductUrl(product.id, product.name, product.brand_name));
      return;
    }
    const stock = product.quantity ?? 0;
    const isOos =
      product.in_stock === false ||
      (product.in_stock === undefined && !product.is_unlimited_stock && stock <= 0);
    if (isOos) return;

    const cartItem = useCartStore.getState().items.find(
      (i) => i.product_id === product.id
    );
    const alreadyInCart = cartItem?.quantity ?? 0;
    if (!product.is_unlimited_stock && alreadyInCart >= stock) {
      toast.error("You already have the maximum available quantity in your cart.", { id: "cart-max-qty" });
      return;
    }

    addToCartStore(product as any, 1);

    // Guests keep items locally; merged into DB after login.
    if (!user) return;

    setAdding((prev) => ({ ...prev, [product.id]: true }));

    const existing = dbSyncTimers.current.get(product.id);
    if (existing) clearTimeout(existing);
    dbSyncTimers.current.set(product.id, setTimeout(async () => {
      const finalQty = useCartStore.getState().items.find(
        (i) => i.product_id === product.id
      )?.quantity ?? 1;
      try {
        await addProductToCart({ productId: product.id, quantity: finalQty });
      } catch {
        toast.error("Failed to add to cart. Please try again.");
      } finally {
        dbSyncTimers.current.delete(product.id);
        setAdding((prev) => ({ ...prev, [product.id]: false }));
      }
    }, 300));
  };

  const renderCard = (product: Product) => {
    const isWishlisted = wishlistedIds?.includes(product.id) || false;
    const cardProps = {
      product,

      adding: !!adding[product.id],
      onAddToCart: () => handleAddToCart(product),
    };

    return viewMode === "list" ? (
      <ProductListCard {...cardProps} />
    ) : (
      <ProductCard
        {...cardProps}
        isWishlisted={isWishlisted}
        isShowWishlist={isShowWishlist}
      />
    );
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <div className="text-lg text-gray-600">Loading products...</div>
        </div>
      </div>
    );
  }

  if (!loading && products.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">No products found</div>
        <div className="text-gray-400 text-sm mt-2">
          Try adjusting your filters
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {filtering && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg shadow-md">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span className="text-gray-600 font-medium">
              Updating results...
            </span>
          </div>
        </div>
      )}

      {is_HScroll ? (
        <>
          {/* Horizontal Scroll on Mobile */}
          <div className="block md:hidden">
            <div
              className={`flex overflow-x-auto gap-4 pb-4 -mx-4 px-6 scroll-pl-6 scroll-pr-6 snap-x snap-mandatory scrollbar-hide ${
                filtering ? "opacity-75" : ""
              }`}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {products.map((product) => (
                <div key={product.id} className="w-[44vw] shrink-0 snap-start">
                  {renderCard(product)}
                </div>
              ))}
            </div>
          </div>

          {/* Grid on Desktop */}
          <div
            className={`hidden md:grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
              filtering ? "opacity-75" : ""
            }`}
          >
            {products.map((product) => (
              <div key={product.id} className="h-full">{renderCard(product)}</div>
            ))}
          </div>
        </>
      ) : (
        <div
          className={`${
            viewMode === "list"
              ? "flex flex-col gap-4 max-w-5xl mx-auto"
              : "grid gap-3 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          } ${filtering ? "opacity-75" : ""}`}
        >
          {products.map((product) => (
            <div key={product.id} className={viewMode === "list" ? "" : "h-full"}>{renderCard(product)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
